import React, { useMemo } from "react";
import { CINEMATIC_STATES, stateForEvent } from "@/lib/decisionGraph";

// Terminal-style HUD strips over the swarm viewport. Every number is derived from the
// REAL ledger events up to the current cursor — nothing is synthesised.
const GOLD = "#f5b942", RED = "#ff4d5e", INK = "#cfc6b0", DIM = "#7b7462";

function family(type) { return String(type || "").split(".")[0] || "run"; }
const STRESS = new Set(["xius", "griffendo"]);

export function HudTop({ runId, moduleId, state, model, events, cursor, live, connection }) {
  const total = events?.length || 0;
  const cur = events?.[Math.min(cursor, total - 1)];
  const s = CINEMATIC_STATES[state] || CINEMATIC_STATES[0];
  const alert = state === 1;
  const items = [
    ["RUN", runId || "—"], ["MODULE", (moduleId || "—").toUpperCase()],
    ["STATE", `${state + 1}/6 ${s.title.toUpperCase()}`],
    ["AGENTS", model?.agentCount ?? 0], ["CANDIDATES", model?.candidateCount ?? 0],
    ["EVENTS", `${total ? Math.min(cursor + 1, total) : 0}/${total}`],
    ["MODE", live ? `LIVE · ${connection || "sse"}` : "REPLAY"],
    ["T", cur?.timestamp ? new Date(cur.timestamp).toISOString().slice(11, 19) : "—"],
  ];
  return (
    <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none px-3 py-1.5 flex items-center gap-4 font-mono text-[9px] tracking-[0.12em] overflow-hidden whitespace-nowrap"
      style={{ background: "linear-gradient(180deg, rgba(4,5,10,.85), rgba(4,5,10,0))", color: INK }} data-testid="dg-hud-top">
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: alert ? RED : GOLD, boxShadow: `0 0 8px ${alert ? RED : GOLD}` }} />
      {items.map(([k, v]) => (
        <span key={k}><span style={{ color: DIM }}>{k} </span><span style={{ color: k === "STATE" && alert ? RED : INK }}>{v}</span></span>
      ))}
    </div>
  );
}

function Panel({ title, children, testid, className = "" }) {
  return (
    <div className={`rounded-sm border px-2 py-1.5 min-w-0 ${className}`} style={{ borderColor: "rgba(207,198,176,.18)", background: "rgba(4,5,10,.72)" }} data-testid={testid}>
      <div className="font-mono text-[8px] tracking-[0.16em] mb-1" style={{ color: DIM }}>{title}</div>
      {children}
    </div>
  );
}

function BarList({ counts, alert }) {
  const max = Math.max(1, ...counts.map((c) => c[1]));
  return (
    <div className="space-y-[3px]">
      {counts.slice(0, 6).map(([k, n]) => (
        <div key={k} className="flex items-center gap-1.5 font-mono text-[8px]" style={{ color: INK }}>
          <span className="w-14 truncate" style={{ color: STRESS.has(k) ? RED : INK }}>{k}</span>
          <span className="flex-1 h-[5px] rounded-[1px]" style={{ background: "rgba(207,198,176,.08)" }}>
            <span className="block h-full rounded-[1px]" style={{ width: `${(n / max) * 100}%`, background: STRESS.has(k) ? RED : (alert ? "#c98a2a" : GOLD) }} />
          </span>
          <span className="w-4 text-right tabular-nums">{n}</span>
        </div>
      ))}
      {!counts.length && <div className="font-mono text-[8px]" style={{ color: DIM }}>no events</div>}
    </div>
  );
}

function Hexagon({ reach, alert }) {
  const R = 22, cx = 28, cy = 26;
  const pt = (i, r) => [cx + r * Math.cos(-Math.PI / 2 + (i * Math.PI) / 3), cy + r * Math.sin(-Math.PI / 2 + (i * Math.PI) / 3)];
  const ring = (r) => Array.from({ length: 6 }, (_, i) => pt(i, r).join(",")).join(" ");
  const fill = Array.from({ length: 6 }, (_, i) => pt(i, 3 + (R - 3) * (reach[i] || 0)).join(",")).join(" ");
  const col = alert ? RED : GOLD;
  return (
    <svg width="56" height="52" aria-label="cinematic state reach">
      {[R, R * 0.66, R * 0.33].map((r) => <polygon key={r} points={ring(r)} fill="none" stroke="rgba(207,198,176,.22)" strokeWidth="0.6" />)}
      {Array.from({ length: 6 }, (_, i) => <line key={i} x1={cx} y1={cy} x2={pt(i, R)[0]} y2={pt(i, R)[1]} stroke="rgba(207,198,176,.14)" strokeWidth="0.6" />)}
      <polygon points={fill} fill={col} fillOpacity="0.45" stroke={col} strokeWidth="1" />
    </svg>
  );
}

function Spark({ series, alert }) {
  const w = 110, h = 30;
  const max = Math.max(1, ...series);
  const pts = series.map((v, i) => `${(i / Math.max(1, series.length - 1)) * w},${h - (v / max) * (h - 3) - 1}`).join(" ");
  const col = alert ? RED : GOLD;
  return (
    <svg width={w} height={h} aria-label="events per interval">
      <polyline points={pts} fill="none" stroke={col} strokeWidth="1.2" strokeLinejoin="round" />
      {series.length > 1 && <circle cx={w} cy={h - (series[series.length - 1] / max) * (h - 3) - 1} r="1.8" fill={col} />}
    </svg>
  );
}

export function HudBottom({ events, cursor, state }) {
  const alert = state === 1;
  const upTo = useMemo(() => (events || []).slice(0, Math.min(cursor + 1, (events || []).length)), [events, cursor]);
  const counts = useMemo(() => {
    const m = new Map();
    upTo.forEach((e) => m.set(family(e.event_type), (m.get(family(e.event_type)) || 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [upTo]);
  const reach = useMemo(() => {
    const r = [0, 0, 0, 0, 0, 0];
    upTo.forEach((e) => { r[stateForEvent(e.event_type)] += 1; });
    const max = Math.max(1, ...r);
    return r.map((v) => v / max);
  }, [upTo]);
  const series = useMemo(() => {
    const n = 12, out = new Array(n).fill(0);
    if (!upTo.length) return out;
    const t0 = new Date(upTo[0].timestamp || 0).getTime(), t1 = new Date(upTo[upTo.length - 1].timestamp || 0).getTime();
    upTo.forEach((e, i) => {
      const t = new Date(e.timestamp || 0).getTime();
      const b = t1 > t0 ? Math.min(n - 1, Math.floor(((t - t0) / (t1 - t0)) * (n - 1))) : Math.min(n - 1, Math.floor((i / upTo.length) * n));
      out[b] += 1;
    });
    return out;
  }, [upTo]);
  const recent = upTo.slice(-4).reverse();
  return (
    <div className="absolute left-3 right-3 z-10 pointer-events-none grid gap-1.5" style={{ bottom: 104, gridTemplateColumns: "1.2fr auto auto 1.6fr" }} data-testid="dg-hud-bottom">
      <Panel title="EVENT MIX" testid="dg-hud-mix"><BarList counts={counts} alert={alert} /></Panel>
      <Panel title="STATE REACH" testid="dg-hud-hex"><Hexagon reach={reach} alert={alert} /></Panel>
      <Panel title="EVENT RATE" testid="dg-hud-spark"><Spark series={series} alert={alert} /></Panel>
      <Panel title="LEDGER TAIL" testid="dg-hud-tail">
        <table className="w-full font-mono text-[8px]" style={{ color: INK }}>
          <tbody>
            {recent.map((e) => (
              <tr key={e.sequence ?? e.event_type + e.timestamp}>
                <td className="pr-2 tabular-nums" style={{ color: DIM }}>#{e.sequence ?? "—"}</td>
                <td className="pr-2 truncate max-w-[140px]" style={{ color: STRESS.has(family(e.event_type)) ? RED : INK }}>{e.event_type}</td>
                <td className="text-right tabular-nums" style={{ color: DIM }}>{e.timestamp ? new Date(e.timestamp).toISOString().slice(11, 19) : ""}</td>
              </tr>
            ))}
            {!recent.length && <tr><td style={{ color: DIM }}>awaiting ledger events</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
